import { useState, useEffect, useRef } from "react";
import { ChatIcon } from "@/assets/Dashboard/chat-icon";
import {
  ArrowUp,
  // ChevronDown,
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  User2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { chatbot } from "@/api/endpoints/chatbot";

const categories = [
  {
    icon: ChatIcon,
    title: "Risk Management",
    questions: [
      "How much should I risk per trade?",
      "What's the safest way to manage drawdown?",
      "Explain position sizing for prop firm challenges.",
    ],
  },
  {
    icon: ChatIcon,
    title: "Trading Psychology",
    questions: [
      "How do I avoid revenge trading?",
      "Tips to stay disciplined during losing streaks?",
      "How to handle FOMO in volatile markets?",
    ],
  },
  {
    icon: ChatIcon,
    title: "Strategy & Setup Guidance",
    questions: [
      "Explain the concept of liquidity zones.",
      "What are the best times to trade major forex pairs?",
      "How do I use risk-to-reward effectively?",
    ],
  },
];

const QUICK_PROMPTS = [
  "How do i manage my risk for this setup?",
  "Any chart patterns forming?",
  "Identify current support and resistance levels",
] as const;

type ChatMode = "advisor" | "mentor";

function getVisibleCount() {
  if (typeof window !== "undefined") {
    if (window.innerWidth >= 1280) return 3;
    if (window.innerWidth >= 768) return 2;
    return 1;
  }
  return 3;
}

function ModePill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-[8px] border px-4 py-1.5 font-neue text-sm transition-colors",
        active
          ? "btn-gradient-left bg-white/[0.06] text-white"
          : " bg-transparent text-white/80 hover:text-white/65 border-transparent",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

const AiCompanion = () => {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(getVisibleCount());
  const [startIdx, setStartIdx] = useState(0);
  const [showBrowseTopics,] = useState(false);
  const [chatMode, setChatMode] = useState<ChatMode>("advisor");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<
    { role: "user" | "ai"; text: string; image?: string; timestamp: Date }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [pendingAiMessage, setPendingAiMessage] = useState<string | null>(null);
  const [typingText, setTypingText] = useState<string>("");
  const [isTyping, setIsTyping] = useState(false);
  const [, setTypingTimestamp] = useState<Date | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [quota, setQuota] = useState<{
    used: number;
    limit: number;
    remaining: number;
  } | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // const formatTimeEST = (date: Date) => {
  //   return date.toLocaleString("en-US", {
  //     timeZone: "America/New_York",
  //     hour: "numeric",
  //     minute: "2-digit",
  //     hour12: true,
  //     month: "short",
  //     day: "numeric",
  //   });
  // };

  useEffect(() => {
    const handleResize = () => setVisibleCount(getVisibleCount());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (startIdx > categories.length - visibleCount) {
      setStartIdx(Math.max(0, categories.length - visibleCount));
    }
  }, [visibleCount, startIdx]);

  const maxStart = categories.length - visibleCount;
  const canGoLeft = startIdx > 0;
  const canGoRight = startIdx < maxStart;
  const visibleCategories = categories.slice(startIdx, startIdx + visibleCount);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isQuotaReached = quota?.remaining === 0;
  const isComposerDisabled =
    loading || pendingAiMessage !== null || isQuotaReached;

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!input.trim() && !selectedImage) || isComposerDisabled) return;

    const currentInput = input.trim() || "Please analyze this image";
    const currentImage = selectedImage;
    const currentImagePreview = imagePreview;

    const userMsg = {
      role: "user" as const,
      text: currentInput,
      image: currentImagePreview || undefined,
      timestamp: new Date(),
    };

    setInput("");
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    setMessages((msgs) => [...msgs, userMsg]);
    setLoading(true);

    try {
      let response;
      if (currentImage) {
        const conversationHistory = messages.map((msg) => ({
          role: msg.role,
          content: msg.text,
        }));
        response = await chatbot.sendImageMessage(
          currentImage,
          currentInput,
          conversationHistory,
        );
      } else {
        response = await chatbot.sendMessage({ message: currentInput });
      }

      const aiText =
        response.data?.response ||
        response.result?.response ||
        response.message ||
        response.response ||
        "I'm sorry, I couldn't process your request.";

      if (response.result?.quota) setQuota(response.result.quota);
      else if (response.data?.quota) setQuota(response.data.quota);

      setPendingAiMessage(aiText);
    } catch {
      setPendingAiMessage(
        "I'm sorry, I encountered an error while processing your request. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionClick = async (question: string) => {
    if (loading || isQuotaReached) return;

    const userMsg = {
      role: "user" as const,
      text: t(question),
      timestamp: new Date(),
    };
    setMessages([userMsg]);
    setLoading(true);

    try {
      const response = await chatbot.sendMessage({ message: t(question) });
      const aiText =
        response.result?.response ||
        response.message ||
        response.response ||
        "I'm sorry, I couldn't process your request.";

      if (response.result?.quota) setQuota(response.result.quota);
      else if (response.data?.quota) setQuota(response.data.quota);

      setPendingAiMessage(aiText);
    } catch {
      setPendingAiMessage(
        "I'm sorry, I encountered an error while processing your request. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!pendingAiMessage) return;
    setTypingText("");
    setIsTyping(true);
    setTypingTimestamp(new Date());
    let idx = 0;
    const interval = setInterval(() => {
      idx++;
      setTypingText(pendingAiMessage.slice(0, idx));
      if (idx >= pendingAiMessage.length) {
        clearInterval(interval);
        setMessages((msgs) => [
          ...msgs,
          { role: "ai", text: pendingAiMessage, timestamp: new Date() },
        ]);
        setPendingAiMessage(null);
        setTypingText("");
        setIsTyping(false);
        setTypingTimestamp(null);
      }
    }, 65);
    return () => clearInterval(interval);
  }, [pendingAiMessage]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isTyping && chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [typingText, isTyping]);

  const renderComposer = (centered: boolean) => (
    <div className={centered ? "w-full " : "w-full"}>
      {isQuotaReached && (
        <div className="mb-3 rounded-xl border border-[#FD675D]/30 bg-[#FD675D]/10 px-4 py-3">
          <p className="text-center font-neue text-sm text-[#FD675D]">
            Your chat limit has been reached. Please try again later.
          </p>
        </div>
      )}

      {imagePreview && (
        <div className="relative mb-3 inline-block rounded-xl border border-white/10 bg-[#0b0b0b]/90 p-4">
          <img
            src={imagePreview}
            alt="Preview"
            className="max-h-32 max-w-xs rounded-lg object-cover"
          />
          <button
            onClick={handleRemoveImage}
            className="absolute right-1 top-1 rounded-full bg-black/70 p-1"
            type="button"
            aria-label="Remove image"
          >
            <X className="h-4 w-4 text-white" />
          </button>
        </div>
      )}

      {/* Quick prompts only on the second (chat) screen, above the composer */}
      {!centered && (
        <div className="mx-auto mt-5 flex w-full mb-7 flex-wrap justify-start gap-2">
          {QUICK_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => handleQuestionClick(prompt)}
              disabled={isComposerDisabled}
              className="h-auto rounded-md border border-white/10 bg-transparent px-3 py-2 text-left font-neue text-sm text-white/85 transition-colors hover:border-white/35 hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {prompt}
            </button>
          ))}
        </div>
      )}


      <form onSubmit={handleSend}>
        <div className="rounded-2xl bg-gradient-to-r from-[#1CCDE6] via-[#9ED473] to-[#DBD633] p-[1px] shadow-[0_0_48px_rgba(158,212,115,0.12)]">
          <div className="flex min-h-[128px] flex-col rounded-[15px] bg-[#0a0a0a]/85 px-4 py-4 backdrop-blur-xl sm:min-h-[128px] sm:px-4 sm:py-4">
            <input
              type="text"
              className="w-full flex-1 bg-transparent font-neue text-sm md:text-base text-white outline-none placeholder:text-white/35 "
              placeholder="Ask anything...."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={isComposerDisabled}
            />

            <div className="mt-4 flex items-end justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center border border-white/10 rounded-[8px]">
                  <ModePill
                    label="Advisor"
                    active={chatMode === "advisor"}
                    onClick={() => setChatMode("advisor")}
                  />
                  <ModePill
                    label="Mentor"
                    active={chatMode === "mentor"}
                    onClick={() => setChatMode("mentor")}
                  />
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isQuotaReached}
                  className="ml-1 rounded-lg p-2 text-white/50 transition-colors hover:bg-white/5 hover:text-white/80 disabled:opacity-40"
                  aria-label="Upload image"
                >
                  <ImagePlus className="h-5 w-5" />
                </button>
              </div>

              <button
                type="submit"
                disabled={
                  (!input.trim() && !selectedImage) || isComposerDisabled
                }
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-b from-[#9ED473] to-[#DBD633] text-[#0a0a0a] shadow-[0_4px_20px_rgba(158,212,115,0.35)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Send message"
              >
                <ArrowUp className="h-5 w-5 stroke-[2.5]" />
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );

  const isEmpty = messages.length === 0;

  return (
    <div className="relative flex h-full min-h-full flex-col overflow-hidden bg-[#050505] text-white bg-[url('/dashboard/chatbgnew.png')] bg-cover bg-center">
      <div className="relative z-10 mx-auto flex h-full w-full max-w-[732px] flex-col px-4 py-6">
        {isEmpty ? (
          <>
            <div className="shrink-0 text-center">
              {quota && (
                <div className="mb-4 flex justify-center">
                  <span className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 font-neue text-xs text-white/55">
                    Chat limit: {quota.used}/{quota.limit} · {quota.remaining} left
                  </span>
                </div>
              )}

              <div className="relative mb-6">

                <div className="relative flex justify-center">
                  <img src={"/dashboard/gradientlogo.svg"} alt="AI Companion" className="h-12 w-12 sm:h-20 sm:w-20" />
                </div>
              </div>

              <h1 className="font-neue text-xl font-medium tracking-tight text-white md:text-[26px]">
                Your AI Trading Companion
              </h1>
              <p className="mx-auto mt-3 max-w-[382px] font-neue text-xs leading-relaxed text-white/90 md:text-sm">
                Mentor, advisor, and coach — all in one place. Switch modes to get the kind of help you need right now.
              </p>
            </div>

            <div className="w-full shrink-0 pb-2 mt-8">
              <div className="mx-auto w-full max-w-[532px]  ">{renderComposer(true)}</div>

              <div className="mx-auto mt-5 flex w-full max-w-[532px]   flex-wrap justify-start gap-2">
                {QUICK_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    onClick={() => handleQuestionClick(prompt)}
                    disabled={isComposerDisabled}
                    className="h-auto rounded-md border border-white/10 bg-transparent px-3 py-2 text-left font-neue text-sm text-white/85 transition-colors hover:border-white/35 hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {prompt}
                  </button>
                ))}
              </div>

              {showBrowseTopics && (
                <div className="mt-6 w-full max-w-5xl text-left">
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                    {visibleCategories.map((cat) => (
                      <div className="w-full" key={cat.title}>
                        <div className="flex flex-col items-center">
                          <cat.icon fill="white" />
                          <h2 className="mt-2 text-center font-neue text-lg font-medium text-white">
                            {t(cat.title)}
                          </h2>
                        </div>
                        <div className="mt-5 flex flex-col gap-2">
                          {cat.questions.map((q, qIdx) => (
                            <button
                              type="button"
                              className={`rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left font-neue text-sm text-white/80 transition-colors hover:border-[#9ED473]/40 hover:bg-[#9ED473]/5 ${isComposerDisabled
                                ? "cursor-not-allowed opacity-50"
                                : ""
                                }`}
                              key={qIdx}
                              onClick={() => handleQuestionClick(q)}
                              disabled={isComposerDisabled}
                            >
                              &ldquo;{t(q)}&rdquo;
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex justify-end gap-3">
                    <button
                      type="button"
                      className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/15 bg-white/[0.04] transition-colors hover:border-white/25 disabled:opacity-30"
                      onClick={() => canGoLeft && setStartIdx(startIdx - 1)}
                      disabled={!canGoLeft}
                      aria-label="Previous topics"
                    >
                      <ChevronLeft className="h-5 w-5 text-white/70" />
                    </button>
                    <button
                      type="button"
                      className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/15 bg-white/[0.04] transition-colors hover:border-white/25 disabled:opacity-30"
                      onClick={() => canGoRight && setStartIdx(startIdx + 1)}
                      disabled={!canGoRight}
                      aria-label="Next topics"
                    >
                      <ChevronRight className="h-5 w-5 text-white/70" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex min-h-0 flex-1 flex-col">
              {quota && (
                <div className="mb-4 flex shrink-0 justify-center">
                  <span className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 font-neue text-xs text-white/55">
                    Chat limit: {quota.used}/{quota.limit} · {quota.remaining} left
                  </span>
                </div>
              )}

              {/* <div className="mb-6 flex shrink-0 items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl btn-gradient-left bg-[#0b0b0b]">
                  <AiIcon fill="#9ED473" className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="font-neue text-lg font-medium text-white">
                    {t("AI Trading Mentor")}
                  </h1>
                  <p className="font-neue text-xs text-white/45">
                    {chatMode === "advisor" ? "Advisor mode" : "Mentor mode"}
                  </p>
                </div>
              </div> */}

              <div
                ref={chatContainerRef}
                className="min-h-0 flex-1 overflow-y-auto hide-scrollbar"
              >
                <div className="flex flex-col gap-6 pb-4">
                  {messages.map((msg, idx) =>
                    msg.role === "user" ? (
                      <div
                        className=" flex max-w-[85%] items-start justify-start gap-3 sm:max-w-[70%]"
                        key={`user-${idx}`}
                      >
                        <div className="border border-white/10 rounded-md p-2 bg-white/[0.04] ml-2">
                          <User2 className="h-4 w-4 shrink-0 text-white" />
                        </div>
                        <div>
                          <div className="text-end">
                            {msg.image && (
                              <img
                                src={msg.image}
                                alt="Uploaded"
                                className="mb-2  block max-w-xs rounded-xl border border-white/10"
                              />
                            )}
                            <p className="inline-block rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-end font-neue text-sm text-white">
                              {msg.text}
                            </p>
                          </div>
                          <p className="mb-1 text-start font-neue text-xs text-white pl-2 pt-1.5">
                            You
                          </p>
                        </div>
                        {/* <PenLine className="mb-6 h-4 w-4 shrink-0 text-white/30" /> */}
                      </div>
                    ) : (
                      <>
                        <div
                          className=" max-w-[85%] sm:max-w-[70%]"
                          key={`ai-${idx}`}
                        >
                          <div className="flex items-start gap-1">
                            <div className="relative flex justify-center">
                              <img src={"/dashboard/gradientlogo.svg"} alt="AI Companion" className="h-8 w-8 sm:h-12 sm:w-12 min-w-12" />
                            </div>
                            <div>
                              <div className="btn-gradient-left !items-start flex flex-col p-3 rounded-xl">
                                <p className="font-neue text-sm leading-relaxed text-white/90">
                                  {msg.text}
                                </p>
                              </div>
                              <p className="mb-1 text-start font-neue text-xs text-white pl-2 pt-1.5">
                                URFX AI
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    ),
                  )}
                  {isTyping && typingText && (
                    <div className="flex max-w-[85%] items-start  sm:max-w-[70%]">
                      <div className="relative flex justify-center">
                        <img src={"/dashboard/gradientlogo.svg"} alt="AI Companion" className="h-8 w-8 sm:h-12 sm:w-12 min-w-12" />
                      </div>
                      <div className="btn-gradient-left !items-start flex flex-col p-3 rounded-xl xl:min-w-[431px]">
                        <p className="mb-1 font-neue text-xs text-white/40">
                          Typing...
                        </p>
                        <p className="font-neue text-sm leading-relaxed text-white/90">
                          {typingText}
                          <span className="animate-pulse">|</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="shrink-0 pt-4">
              {renderComposer(false)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AiCompanion;
